import {
  ClientExceptionEntityTypeEnum,
  ClientOrganizationProfileRoleEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, Divider, RadioButton, Text } from 'react-native-paper';

import { getClientErrorType } from '@/features/api/hooks/errorMiddleware';
import { useChangeOrganizationMemberRole } from '@/features/api/hooks/grapevine-organization/useChangeOrganizationMemberRole';
import type { ClientOrganizationMember } from '@/features/api/hooks/grapevine-organization/useGetOrganizationMembers';
import { BottomModal } from '@/features/popup/components/BottomModal';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = {
  organizationId: string;
  member: ClientOrganizationMember;
  currentRoleKey: ClientOrganizationProfileRoleEnum;
  /**
   * True when the member is the only admin. Blocks a demotion (admin → funker)
   * up-front with the cannotDemoteLastAdmin alert instead of firing the PATCH.
   */
  isLastAdmin: boolean;
  isVisible: boolean;
  onClose: () => void;
};

/**
 * Bottom-sheet role picker. Pre-selects the current role, disables Apply when
 * the selection is unchanged, and fires the PATCH mutation on Apply. Closes on
 * success. Errors surface via alerts owned by this sheet so the row menu that
 * launched it doesn't need to know they can happen.
 */
export const ChangeRoleModal: FC<Props> = ({
  organizationId,
  member,
  currentRoleKey,
  isLastAdmin,
  isVisible,
  onClose
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-members'
  });
  const { t: tRoles } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.roleLabels'
  });
  const { showAlert } = usePopup();

  const [selectedRole, setSelectedRole] =
    useState<ClientOrganizationProfileRoleEnum>(currentRoleKey);

  useEffect(() => {
    if (isVisible) setSelectedRole(currentRoleKey);
  }, [currentRoleKey, isVisible]);

  const { mutate: changeRole, isPending } = useChangeOrganizationMemberRole();

  const displayName = useMemo(
    () => [member.firstName, member.lastName].filter(Boolean).join(' '),
    [member.firstName, member.lastName]
  );

  const isDemotion =
    currentRoleKey === ClientOrganizationProfileRoleEnum.Admin &&
    selectedRole === ClientOrganizationProfileRoleEnum.Funker;
  const isUnchanged = selectedRole === currentRoleKey;

  const handleApply = useCallback(() => {
    if (isUnchanged) {
      onClose();
      return;
    }
    if (isDemotion && isLastAdmin) {
      onClose();
      showAlert({
        dismissable: true,
        labels: t('alerts.cannotDemoteLastAdmin', { returnObjects: true })
      });
      return;
    }
    changeRole(
      {
        organizationId,
        roleAssignmentId: member.roleAssignmentId,
        roleKey: selectedRole
      },
      {
        onSuccess: () => onClose(),
        onError: (error) => {
          onClose();
          const type = getClientErrorType(error);
          if (type === ClientExceptionEntityTypeEnum.LastAdminCannotBeRemoved) {
            showAlert({
              dismissable: true,
              labels: t('alerts.cannotDemoteLastAdmin', {
                returnObjects: true
              })
            });
          } else {
            showAlert({
              dismissable: true,
              labels: t('alerts.actionFailed', { returnObjects: true })
            });
          }
        }
      }
    );
  }, [
    changeRole,
    isDemotion,
    isLastAdmin,
    isUnchanged,
    member.roleAssignmentId,
    onClose,
    organizationId,
    selectedRole,
    showAlert,
    t
  ]);

  return (
    <BottomModal
      isVisible={isVisible}
      title={t('changeRoleSheet.title')}
      titleAlign="center"
      showCloseButton={false}
      onClose={onClose}
    >
      <View style={styles.body}>
        <Text variant="bodyMedium" style={styles.description}>
          <Trans
            i18nKey="profile-setting:screens.organization-members.changeRoleSheet.description"
            values={{ name: displayName }}
            components={{ bold: <Text style={styles.bold}>{''}</Text> }}
          />
        </Text>

        <RadioButton.Group
          value={selectedRole}
          onValueChange={(v) =>
            setSelectedRole(v as ClientOrganizationProfileRoleEnum)
          }
        >
          <RadioButton.Item
            label={tRoles('ADMIN')}
            value={ClientOrganizationProfileRoleEnum.Admin}
            labelVariant="bodyLarge"
            position="leading"
            labelStyle={styles.radioLabel}
            style={styles.radioItem}
          />
          <Divider />
          <RadioButton.Item
            label={tRoles('FUNKER')}
            value={ClientOrganizationProfileRoleEnum.Funker}
            labelVariant="bodyLarge"
            position="leading"
            labelStyle={styles.radioLabel}
            style={styles.radioItem}
          />
        </RadioButton.Group>

        <View style={styles.actions}>
          <Button
            mode="text"
            onPress={onClose}
            disabled={isPending}
            style={styles.actionButton}
          >
            {t('changeRoleSheet.cancel')}
          </Button>
          <Button
            mode="contained"
            onPress={handleApply}
            loading={isPending}
            disabled={isPending}
            style={styles.actionButton}
          >
            {t('changeRoleSheet.apply')}
          </Button>
        </View>
      </View>
    </BottomModal>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    body: {
      paddingHorizontal: 24,
      paddingBottom: 24,
      gap: 16
    },
    description: {
      color: theme.colors.onSurfaceVariant
    },
    bold: { fontWeight: 'bold' },
    radioItem: {
      paddingHorizontal: 0,
      paddingVertical: 8
    },
    radioLabel: {
      textAlign: 'left'
    },
    actions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: 8,
      paddingTop: 8
    },
    actionButton: {
      minWidth: 100
    }
  });
